import SortDownIcon from '@isbj-redesign/icons/FaSortDownSolid'
import SortUpIcon from '@isbj-redesign/icons/FaSortUpSolid'
import React, { ReactNode, useCallback, useMemo, useState } from 'react'

export type SortDirection = 'asc' | 'desc'

export type FieldSort<SortField extends string = string> = {
    field: SortField
    direction: SortDirection
}

export default function SortableTableHeader<SortField extends string>({
    sort,
    setSort,
    field,
    children,
}: {
    sort?: FieldSort<SortField>[]
    setSort(sort: FieldSort<SortField>[]): void
    field: SortField
    children: ReactNode
}) {
    // the current sort directive of the query
    const currentSort = sort && sort.length ? sort[0] : undefined

    // the current sort direction of this field
    const order = getOrder(field, currentSort)

    const doSort = useCallback(
        () => setSort([{ field, direction: currentSort?.field === field ? (currentSort.direction === 'desc' ? 'asc' : 'desc') : 'asc' }]),
        [currentSort, field, setSort]
    )

    return (
        <th aria-sort={getAriaSort(order)}>
            <button type="button" onClick={doSort} className="btn btn-link p-0 d-flex font-weight-bold text-body">
                {children} <SortIcon direction={order} className="ml-1" />
            </button>
        </th>
    )
}

export function useSort<SortField extends string>(defaultSort: FieldSort<SortField>[] = []) {
    const [sort, setSort] = useState<FieldSort<SortField>[]>(defaultSort)
    return useMemo(
        () => ({
            parameter: sort ? sort.map((s) => `${s.direction === 'desc' ? '-' : ''}${s.field}`).join(',') : undefined,
            sort,
            setSort,
        }),
        [sort]
    )
}

const getAriaSort = (order?: SortDirection): React.AriaAttributes['aria-sort'] => {
    return (order && (`${order}ending` as React.AriaAttributes['aria-sort'])) || 'none'
}

function getOrder<SortField extends string>(feld: string, sort?: FieldSort<SortField>): SortDirection | undefined {
    return sort && sort.field === feld ? sort.direction : undefined
}

export function SortIcon({ direction, ...props }: { direction?: SortDirection; className?: string }) {
    if (direction === 'asc') {
        return <SortUpIcon {...props} />
    }

    if (direction === 'desc') {
        return <SortDownIcon {...props} />
    }

    return null
}
